import { createRouter, createWebHistory, type RouteRecordRaw, type RouteLocationNormalized, type NavigationGuardNext } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { usePermissions } from '@/composables/usePermissions'
import { routeParamProjectIdFromRoute, syncCurrentProjectWithRouteParam } from '@/utils/projectRouteSync'
import { getAccessToken } from '@/utils/httpClient'
import { db } from '@/database'
import { setPageTitle } from '@/composables/usePageTitle'
import { trackAuthRedirectToLogin } from '@/telemetry/authTelemetry'
import { createRoutePageViewTracker, getFrontendRoute } from '@/telemetry/routeAnalytics'

// Lazy-loaded views — keep all routes code-split so the initial bundle stays small
const Login = () => import('@/views/auth/Login.vue')
const OldLogin = () => import('@/views/auth/OldLogin.vue')
const Register = () => import('@/views/auth/Register.vue')
const ForgotPassword = () => import('@/views/auth/ForgotPassword.vue')
const ResetPassword = () => import('@/views/auth/ResetPassword.vue')
const ChangePassword = () => import('@/views/auth/ChangePassword.vue')
const Dashboard = () => import('@/views/dashboard/Dashboard.vue')
const MyTasks = () => import('@/views/my-tasks/MyTasks.vue')
const ProjectDetail = () => import('@/views/projects/ProjectDetail.vue')
const TestRuns = () => import('@/views/test-runs/TestRuns.vue')
const TestRunDetail = () => import('@/views/test-runs/TestRunDetail.vue')
const TestCases = () => import('@/views/test-cases/TestCases.vue')
const TestCaseDetail = () => import('@/views/test-cases/TestCaseDetail.vue')
const Bugs = () => import('@/views/bugs/Bugs.vue')
const BugDetail = () => import('@/views/bugs/BugDetail.vue')
const Reports = () => import('@/views/reports/Reports.vue')
const Profile = () => import('@/views/profile/Profile.vue')
const CrossTeamSanity = () => import('@/views/cross-team-sanity/CrossTeamSanity.vue')
const IntegrationsView = () => import('@/views/integrations/IntegrationsView.vue')
const ChromeExtension = () => import('@/views/tools/ChromeExtension.vue')
const NotFound = () => import('@/views/errors/NotFound.vue')

// Extend route meta interface
declare module 'vue-router' {
  interface RouteMeta {
    requiresAuth?: boolean
    requiresGuest?: boolean
    requiresPermission?: string
    requiresTeamOrProjectPermission?: boolean
  }
}

const routes: RouteRecordRaw[] = [
  // Authentication routes
  {
    path: '/login',
    name: 'Login',
    component: Login,
    beforeEnter: (to, from, next) => {
      if (import.meta.env.DEV) {
        next('/old-login')
        return
      }
      next()
    },
    meta: { requiresGuest: true },
  },
  {
    path: '/old-login',
    name: 'OldLogin',
    component: OldLogin,
    meta: { requiresGuest: true },
  },
  {
    path: '/create-new-boss',
    name: 'Register',
    component: Register,
    meta: { requiresGuest: true },
  },
  {
    path: '/register',
    redirect: '/login',
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    component: ForgotPassword,
    meta: { requiresGuest: true },
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: ResetPassword,
    meta: { requiresGuest: true },
  },
  {
    path: '/change-password',
    name: 'ChangePassword',
    component: ChangePassword,
    meta: { requiresAuth: true },
  },

  // Main application routes
  {
    path: '/',
    redirect: '/dashboard',
  },
  {
    path: '/dashboard',
    name: 'Dashboard',
    component: Dashboard,
    meta: { requiresAuth: true },
  },
  {
    path: '/projects',
    name: 'Projects',
    component: () => import('@/views/projects/Projects.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/my-tasks',
    name: 'MyTasks',
    component: MyTasks,
    meta: { requiresAuth: true },
  },
  {
    path: '/assigned-to-me',
    redirect: '/my-tasks',
  },
  {
    path: '/cross-team-sanity',
    name: 'CrossTeamSanity',
    component: CrossTeamSanity,
    meta: { requiresAuth: true },
  },
  {
    path: '/cross-team-sanity/:id',
    name: 'CrossTeamSanityDetail',
    component: () => import('@/views/cross-team-sanity/CrossTeamSanityDetail.vue'),
    meta: { requiresAuth: true },
  },

  // Project routes
  {
    path: '/projects/:projectId',
    name: 'ProjectDetail',
    component: ProjectDetail,
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        redirect: to => {
          const raw = to.params.projectId
          const projectId = typeof raw === 'string' ? raw : Array.isArray(raw) && raw[0] ? raw[0] : ''
          if (!projectId) return { path: '/projects', query: to.query, hash: to.hash }
          return {
            path: `/projects/${projectId}/overview`,
            query: to.query,
            hash: to.hash,
          }
        },
      },
      {
        path: 'overview',
        name: 'ProjectOverview',
        component: () => import('@/views/projects/ProjectOverview.vue'),
      },
      {
        path: 'reports',
        name: 'ProjectReports',
        component: () => import('@/views/projects/ProjectReports.vue'),
      },
      {
        path: 'test-runs',
        name: 'ProjectTestRuns',
        component: () => import('@/views/projects/ProjectTestRuns.vue'),
      },
      {
        path: 'test-cases',
        name: 'ProjectTestCases',
        component: () => import('@/views/projects/ProjectTestCases.vue'),
      },
      {
        path: 'features',
        name: 'ProjectFeatures',
        component: () => import('@/views/projects/ProjectFeatures.vue'),
      },
      {
        path: 'ideas-board',
        name: 'ProjectIdeasBoard',
        component: () => import('@/views/ideas/IdeasBoard.vue'),
      },
      {
        path: 'bugs',
        name: 'ProjectBugs',
        component: () => import('@/views/projects/ProjectBugs.vue'),
      },
      {
        path: 'settings',
        name: 'ProjectSettings',
        component: () => import('@/views/projects/ProjectSettings.vue'),
        meta: { requiresPermission: 'viewSettings' },
      },
      {
        path: 'users',
        name: 'ProjectUsers',
        component: () => import('@/views/projects/ProjectUsers.vue'),
      },
      {
        path: 'accessibility',
        name: 'ProjectAccessibility',
        component: () => import('@/views/accessibility/Accessibility.vue'),
      },
    ],
  },

  // Test Runs routes
  {
    path: '/test-runs',
    name: 'TestRuns',
    component: TestRuns,
    meta: { requiresAuth: true },
  },
  {
    path: '/test-runs/:runId',
    name: 'TestRunDetail',
    component: TestRunDetail,
    meta: { requiresAuth: true },
  },
  {
    path: '/projects/:projectId/test-runs/:runId',
    name: 'ProjectTestRunDetail',
    component: TestRunDetail,
    meta: { requiresAuth: true },
  },
  {
    path: '/projects/:projectId/test-runs/create',
    name: 'CreateTestRun',
    component: () => import('@/views/test-runs/CreateTestRun.vue'),
    meta: { requiresAuth: true, requiresPermission: 'createTestRuns' },
  },
  {
    path: '/projects/:projectId/test-runs/:runId/edit',
    name: 'EditTestRun',
    component: () => import('@/views/test-runs/EditTestRun.vue'),
    meta: { requiresAuth: true, requiresPermission: 'editTestRuns' },
  },
  {
    path: '/test-runs/:runId/edit',
    name: 'EditTestRunGeneral',
    component: () => import('@/views/test-runs/EditTestRun.vue'),
    meta: { requiresAuth: true },
  },

  // Feature routes
  {
    path: '/projects/:projectId/features/create',
    name: 'CreateFeature',
    component: () => import('@/views/features/CreateFeature.vue'),
    meta: { requiresAuth: true, requiresPermission: 'createFeatures' },
  },
  {
    path: '/projects/:projectId/features/:featureId',
    name: 'FeatureDetail',
    component: () => import('@/views/features/FeatureDetail.vue'),
    meta: { requiresAuth: true },
  },

  // Test Cases routes
  {
    path: '/projects/:projectId/test-cases',
    name: 'TestCases',
    component: TestCases,
    meta: { requiresAuth: true },
  },
  {
    path: '/projects/:projectId/test-cases/create',
    name: 'CreateTestCase',
    component: () => import('@/views/test-cases/CreateTestCase.vue'),
    meta: { requiresAuth: true, requiresPermission: 'createTestCases' },
  },
  {
    path: '/projects/:projectId/test-cases/:caseId',
    name: 'TestCaseDetail',
    component: TestCaseDetail,
    meta: { requiresAuth: true },
  },

  // Bugs routes
  {
    path: '/bugs',
    name: 'Bugs',
    component: Bugs,
    meta: { requiresAuth: true },
  },
  {
    path: '/bugs/:bugId',
    name: 'BugDetail',
    component: BugDetail,
    meta: { requiresAuth: true },
  },
  {
    path: '/projects/:projectId/bugs/create',
    name: 'CreateBug',
    component: () => import('@/views/bugs/CreateBug.vue'),
    meta: { requiresAuth: true, requiresPermission: 'createBugs' },
  },
  {
    path: '/projects/:projectId/bugs/:bugId',
    name: 'ProjectBugDetail',
    component: BugDetail,
    meta: { requiresAuth: true },
  },

  // Reports routes
  {
    path: '/reports',
    name: 'Reports',
    component: Reports,
    meta: { requiresAuth: true },
  },

  // Accessibility route
  {
    path: '/accessibility',
    name: 'Accessibility',
    component: () => import('@/views/accessibility/Accessibility.vue'),
    meta: { requiresAuth: true },
  },

  // Learning Hub route
  {
    path: '/learning-hub',
    name: 'LearningHub',
    component: () => import('@/views/learning-hub/LearningHub.vue'),
    meta: { requiresAuth: true },
  },

  // Performance route
  {
    path: '/performance',
    name: 'Performance',
    component: () => import('@/views/performance/Performance.vue'),
    meta: { requiresAuth: true },
  },

  // Ideas Board route (top-level)
  {
    path: '/ideas-board',
    name: 'IdeasBoard',
    component: () => import('@/views/ideas/IdeasBoard.vue'),
    meta: { requiresAuth: true },
  },

  // Profile route
  {
    path: '/profile',
    name: 'Profile',
    component: Profile,
    meta: { requiresAuth: true },
  },

  // Teams route
  {
    path: '/teams',
    name: 'Teams',
    component: () => import('@/views/teams/Teams.vue'),
    meta: {
      requiresAuth: true,
      requiresTeamOrProjectPermission: true,
    },
  },

  // Team Details route
  {
    path: '/teams/:id',
    name: 'TeamDetails',
    component: () => import('@/views/teams/TeamDetails.vue'),
    meta: {
      requiresAuth: true,
      requiresTeamOrProjectPermission: true,
    },
  },

  // Users Management route
  {
    path: '/users',
    name: 'Users',
    component: () => import('@/views/users/Users.vue'),
    meta: { requiresAuth: true, requiresPermission: 'viewUsers' },
  },

  // Release Manager
  {
    path: '/release-manager',
    name: 'ReleaseManager',
    component: () => import('@/views/releases/ReleaseManager.vue'),
    meta: { requiresAuth: true },
  },

  // Using MongoDB database only

  // Integrations routes
  {
    path: '/integrations',
    redirect: '/integrations/slack/my-analytics',
  },
  {
    path: '/slack',
    redirect: '/slack/my-analytics',
  },
  {
    path: '/slack/my-analytics',
    alias: '/integrations/slack/my-analytics',
    name: 'SlackMyAnalytics',
    component: IntegrationsView,
    meta: { requiresAuth: true },
  },
  {
    path: '/slack/team-analytics',
    alias: '/integrations/slack/team-analytics',
    name: 'SlackTeamAnalytics',
    component: IntegrationsView,
    meta: { requiresAuth: true },
  },
  {
    path: '/integrations/playwright-report',
    name: 'PlaywrightReport',
    component: () => import('@/views/ai/PlaywrightReportView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/dashboard/integrations',
    redirect: '/integrations/slack/my-analytics',
  },
  {
    path: '/assistant',
    redirect: '/integrations/slack/my-analytics',
  },
  {
    path: '/feedback',
    name: 'Feedback',
    component: () => import('@/views/feedback/Feedback.vue'),
    meta: { requiresAuth: true },
    beforeEnter: async (to, from, next) => {
      const { isRole } = usePermissions()
      if (!isRole.superAdmin.value) {
        const { useToast } = await import('@/composables/useToast')
        const { error } = useToast()
        error('Only super admins can view feedback')
        next(from.path && from.path !== '/' ? from.path : '/dashboard')
        return
      }
      next()
    },
  },
  // Chrome Extension (tools)
  {
    path: '/chrome-extension',
    alias: '/integrations/chrome-extension',
    name: 'ChromeExtension',
    component: ChromeExtension,
    meta: { requiresAuth: true },
  },

  // 404 route
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    beforeEnter: to => {
      const requestedPath = String(to.path || '').toLowerCase()
      // Graceful fallback for common assistant URL variants/typos.
      if (requestedPath.includes('assistant')) {
        return '/integrations'
      }
      return true
    },
    component: NotFound,
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return { top: 0 }
    }
  },
})

router.afterEach(to => {
  syncCurrentProjectWithRouteParam(routeParamProjectIdFromRoute(to))
})

// Navigation guards
router.beforeEach(async (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
  // Note: to and from parameters are used below in conditional checks
  const authStore = useAuthStore()

  // Initialize auth if not already initialized (ensures token is set for API calls)
  if (!authStore.user && !authStore.loading) {
    await authStore.initializeAuth()
  }

  // Wait for auth to initialize if it's loading
  if (authStore.loading) {
    await new Promise<void>(resolve => {
      const unwatch = authStore.$subscribe(() => {
        if (!authStore.loading) {
          unwatch()
          resolve()
        }
      })
    })
  }

  // Ensure token is set on HTTP adapter even if user is already authenticated
  // This handles navigation after login when user is set but token might not be on adapter
  if (authStore.isAuthenticated && !authStore.loading) {
    const token = getAccessToken()
    if (token) {
      db.setAuthToken(token)
    }
  }

  // No special handling needed for root path since it redirects to /dashboard

  // Check authentication requirements
  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    trackAuthRedirectToLogin('guard_requires_auth', {
      frontendRoute: getFrontendRoute(to),
      routeName: typeof to.name === 'string' ? to.name : undefined,
    })

    // Store the intended URL for redirect after login
    if (to.fullPath && to.fullPath !== '/login' && to.fullPath !== '/') {
      localStorage.setItem('redirect_after_login', to.fullPath)
    }
    next('/login')
    return
  }

  if (to.meta.requiresGuest && authStore.isAuthenticated) {
    next('/dashboard')
    return
  }

  // Check permission requirements for authenticated users
  if (to.meta.requiresPermission && authStore.isAuthenticated) {
    const { can } = usePermissions()
    const permissionMethod = to.meta.requiresPermission as keyof typeof can

    // Check if the permission method exists and user has permission
    if (typeof can[permissionMethod] === 'function' && !can[permissionMethod]()) {
      // Show error notification
      const { useToast } = await import('@/composables/useToast')
      const { error } = useToast()
      error('You do not have permission to access this page')

      // Redirect to previous page or dashboard
      if (from.path && from.path !== '/' && from.path !== '/dashboard') {
        next(from.path)
      } else {
        next('/dashboard')
      }
      return
    }
  }

  // Check team/project creation permission for teams routes
  if (to.meta.requiresTeamOrProjectPermission && authStore.isAuthenticated) {
    const { can } = usePermissions()

    // User must have at least read access to teams or projects
    if (!can.viewTeams() && !can.viewProjects()) {
      // Show error notification
      const { useToast } = await import('@/composables/useToast')
      const { error } = useToast()
      error('You do not have permission to access Teams & Projects management')

      // Redirect to dashboard
      next('/dashboard')
      return
    }
  }

  next()
})

// Set default page titles based on route
const routeTitleMap: Record<string, string> = {
  Dashboard: 'Dashboard',
  Login: 'Login',
  Register: 'Register',
  ForgotPassword: 'Forgot Password',
  ResetPassword: 'Reset Password',
  ChangePassword: 'Change Password',
  MyTasks: 'My Tasks',
  AssignedToMe: 'Assigned to Me',
  ProjectDetail: 'Project',
  ProjectOverview: 'Project Overview',
  ProjectTestRuns: 'Test Runs',
  ProjectTestCases: 'Test Cases',
  ProjectBugs: 'Bugs',
  ProjectFeatures: 'Features',
  ProjectSettings: 'Project Settings',
  ProjectUsers: 'Users',
  ProjectReports: 'Project Reports',
  TestRuns: 'Test Runs',
  TestRunDetail: 'Test Run',
  ProjectTestRunDetail: 'Test Run',
  CreateTestRun: 'Create Test Run',
  EditTestRun: 'Edit Test Run',
  EditTestRunGeneral: 'Edit Test Run',
  TestCases: 'Test Cases',
  TestCaseDetail: 'Test Case',
  CreateTestCase: 'Create Test Case',
  Bugs: 'Bugs',
  BugDetail: 'Bug',
  ProjectBugDetail: 'Bug',
  CreateBug: 'Create Bug',
  FeatureDetail: 'Feature',
  CreateFeature: 'Create Feature',
  Reports: 'Reports',
  Accessibility: 'Accessibility',
  Profile: 'Profile',
  Teams: 'Teams',
  TeamDetails: 'Team Details',
  Users: 'Users',
  Notifications: 'Notifications',
  Integrations: 'Integrations',
  JenkinsPipelines: 'Jenkins Pipelines',
  JenkinsAnalytics: 'Jenkins Analytics',
  GithubPullRequests: 'GitHub Pull Requests',
  GithubAnalytics: 'GitHub Analytics',
  SlackMyAnalytics: 'Slack My Analytics',
  SlackTeamAnalytics: 'Slack Team Analytics',
  PlaywrightReport: 'Playwright Report',
  CrossTeamSanity: 'Cross Team Sanity',
  Feedback: 'Feedback',
  ChromeExtension: 'Chrome Extension',
  IdeasBoard: 'Ideas Board',
  NotFound: 'Page Not Found',
}

const trackRoutePageView = createRoutePageViewTracker()

router.afterEach(to => {
  const routeTitle = routeTitleMap[to.name as string] || ''
  const isDetailPage = to.params.runId || to.params.bugId || to.params.featureId || to.params.caseId || to.params.id || to.params.projectId
  if (!isDetailPage || to.name === 'ProjectDetail') {
    setPageTitle(routeTitle)
  }
  trackRoutePageView(to, routeTitleMap)
})

export default router
